import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Users, Lock, Star, TrendingUp, Flame, Award } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { GradeBadge } from '../components/GradeBadge';
import { useGamification } from '../context/GamificationContext';

const API = `${''}/api`;

const POINT_LABELS = {
  feedback_error: 'Signaler une erreur / hallucination',
  error_validated_bonus: 'Bonus : erreur validée par l’équipe',
  feedback_source: 'Proposer une source fiable',
  feedback_suggestion: 'Suggestion via l’Assistant Progrès',
  feedback_bug: 'Signaler un bug technique',
  rating: 'Noter une réponse de l’IA',
  weekly_streak: 'Série hebdomadaire de contribution',
};
const POINT_ORDER = ['feedback_error', 'error_validated_bonus', 'feedback_source', 'feedback_suggestion', 'feedback_bug', 'rating', 'weekly_streak'];

export const ProgressPage = () => {
  const { progress, refresh } = useGamification();
  const [leaderboard, setLeaderboard] = useState([]);
  const [pseudo, setPseudo] = useState('');
  const [optIn, setOptIn] = useState(false);

  useEffect(() => { refresh?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (progress) { setOptIn(progress.leaderboard_opt_in); setPseudo(progress.pseudo || ''); }
  }, [progress]);

  const loadLb = async () => {
    try { const r = await axios.get(`${API}/leaderboard`); setLeaderboard(r.data.leaderboard || []); } catch (e) { /* */ }
  };
  useEffect(() => { loadLb(); }, [progress?.leaderboard_opt_in]);

  const saveOptIn = async (val) => {
    try {
      const r = await axios.post(`${API}/progress/optin`, { opt_in: val, pseudo: pseudo || undefined });
      setOptIn(r.data.leaderboard_opt_in);
      setPseudo(r.data.pseudo || '');
      toast.success(val ? 'Vous participez au classement' : 'Vous avez quitté le classement');
      refresh?.();
      loadLb();
    } catch (e) { toast.error('Erreur'); }
  };

  if (!progress || !progress.grade) {
    return <div data-testid="progress-page" className="py-20 text-center text-slate-400">Chargement de votre progression…</div>;
  }

  const g = progress.grade;
  const nxt = progress.next_grade;
  const allGrades = progress.all_grades || [];
  const currentIdx = g.index ?? 0;
  const pointsTable = progress.points_table || {};
  const bonusBadges = progress.bonus_badges || [];
  const badgeMetrics = progress.badge_metrics || {};

  return (
    <div data-testid="progress-page" className="space-y-4 sm:space-y-8">
      <PageHeader icon={Trophy} title="Ma Progression" subtitle="Parcours d'Excellence Médicale" gradient="from-amber-500 to-yellow-600" testId="progress-header" />

      {/* Hero grade */}
      <div className="glassmorphic rounded-2xl p-5 sm:p-7" data-testid="progress-hero">
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <img src={g.badge} alt={g.nom} className="w-28 h-28 sm:w-32 sm:h-32 object-contain drop-shadow-[0_0_24px_rgba(251,191,36,0.35)] shrink-0" />
          <div className="flex-1 w-full text-center sm:text-left">
            <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Grade actuel</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">{g.nom}</h2>
            <p className="text-slate-400 italic text-sm">{g.tagline}</p>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
                <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400" /> {progress.xp} points</span>
                {nxt ? <span>{progress.xp_to_next} pts → {nxt.nom}</span> : <span className="text-amber-300">Grade maximum atteint 🏆</span>}
              </div>
              <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 transition-all duration-700" style={{ width: `${progress.progress_pct}%` }} data-testid="progress-bar" />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 justify-center sm:justify-start text-xs text-slate-400">
              <span className="inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> {progress.contributions || 0} contributions</span>
              {(progress.streak_current || 0) > 0 && (
                <span className="inline-flex items-center gap-1 text-orange-300" data-testid="streak-indicator">
                  <Flame className="w-3.5 h-3.5 text-orange-400" /> Série : {progress.streak_current} sem.{(progress.streak_best || 0) > (progress.streak_current || 0) ? ` (record ${progress.streak_best})` : ''}
                </span>
              )}
              {progress.rank && <span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-amber-400" /> {progress.rank}ᵉ au classement</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Grade ladder */}
        <div className="lg:col-span-2 glassmorphic rounded-2xl p-4 sm:p-6">
          <h3 className="text-white font-semibold mb-4">Le Parcours</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {allGrades.map((grade, idx) => {
              const unlocked = idx <= currentIdx;
              return (
                <div key={grade.slug} data-testid={`ladder-grade-${idx}`} className={`relative rounded-xl border p-3 text-center transition-all ${idx === currentIdx ? 'border-amber-500/60 bg-amber-500/5' : unlocked ? 'border-slate-700 bg-slate-800/30' : 'border-slate-800 bg-slate-900/40'}`}>
                  <div className="relative mx-auto w-16 h-16 mb-2">
                    <img src={grade.badge} alt={grade.nom} className={`w-full h-full object-contain ${unlocked ? '' : 'grayscale opacity-40'}`} />
                    {!unlocked && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-5 h-5 text-slate-500" /></div>}
                  </div>
                  <p className={`text-xs font-medium leading-tight ${unlocked ? 'text-white' : 'text-slate-500'}`}>{grade.nom}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{grade.min_xp} pts</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* How to earn points */}
        <div className="glassmorphic rounded-2xl p-4 sm:p-6">
          <h3 className="text-white font-semibold mb-4">Gagner des points</h3>
          <ul className="space-y-2.5">
            {POINT_ORDER.filter((k) => pointsTable[k]).map((k) => (
              <li key={k} className="flex items-start justify-between gap-2 text-sm">
                <span className="text-slate-300">{POINT_LABELS[k]}</span>
                <span className="text-amber-400 font-semibold whitespace-nowrap">+{pointsTable[k]}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
            Les points récompensent la qualité de vos contributions (retours, signalements, sources) — pas le volume d'usage clinique.
          </p>
        </div>
      </div>

      {/* Badges bonus + Piliers par service */}
      {[
        { key: 'bonus', title: 'Badges bonus', icon: Award, accent: 'text-violet-400', testId: 'bonus-badges-section', note: 'Les badges récompensent vos jalons de qualité et votre régularité hebdomadaire — chaque contribution compte.' },
        { key: 'service', title: 'Piliers par service', icon: Award, accent: 'text-cyan-400', testId: 'service-badges-section', note: 'Devenez le « Pilier » d’un service en lui donnant régulièrement vos retours (notes et signalements).' },
      ].map((grp) => {
        const list = bonusBadges.filter((b) => (b.group || 'bonus') === grp.key);
        if (list.length === 0) return null;
        const Icon = grp.icon;
        return (
          <div key={grp.key} className="glassmorphic rounded-2xl p-4 sm:p-6" data-testid={grp.testId}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2"><Icon className={`w-5 h-5 ${grp.accent}`} /> {grp.title}</h3>
              <span className="text-xs text-slate-400">{list.filter((b) => b.unlocked).length}/{list.length} débloqués</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {list.map((b) => {
                const val = Math.min(badgeMetrics[b.metric] ?? 0, b.threshold);
                const pct = Math.round((val / b.threshold) * 100);
                return (
                  <div
                    key={b.slug}
                    data-testid={`bonus-badge-${b.slug}`}
                    className={`relative rounded-xl border p-3 text-center transition-all ${b.unlocked ? 'border-violet-500/50 bg-violet-500/5' : 'border-slate-800 bg-slate-900/40'}`}
                  >
                    <div className="relative mx-auto w-16 h-16 mb-2">
                      <img src={b.badge} alt={b.nom} className={`w-full h-full object-contain ${b.unlocked ? 'drop-shadow-[0_0_14px_rgba(167,139,250,0.35)]' : 'grayscale opacity-40'}`} />
                      {!b.unlocked && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-5 h-5 text-slate-500" /></div>}
                    </div>
                    <p className={`text-xs font-medium leading-tight ${b.unlocked ? 'text-white' : 'text-slate-400'}`}>{b.nom}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{b.critere}</p>
                    {b.unlocked ? (
                      <p className="text-[10px] text-violet-300 mt-1 font-medium">Débloqué ✓</p>
                    ) : (
                      <>
                        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden mt-2">
                          <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-400 transition-all duration-500" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">{val}/{b.threshold}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">{grp.note}</p>
          </div>
        );
      })}

      {/* Leaderboard */}
      <div className="glassmorphic rounded-2xl p-4 sm:p-6" data-testid="leaderboard-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-cyan-400" /> Classement entre confrères</h3>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="leaderboard-optin">
            <input type="checkbox" checked={optIn} onChange={(e) => saveOptIn(e.target.checked)} className="w-4 h-4 accent-cyan-500" />
            Participer (pseudonyme)
          </label>
        </div>

        {optIn && (
          <div className="flex gap-2 mb-4">
            <input
              value={pseudo}
              onChange={(e) => setPseudo(e.target.value)}
              placeholder="Votre pseudonyme"
              className="flex-1 px-3 py-2 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-200 text-sm"
              data-testid="pseudo-input"
            />
            <button onClick={() => saveOptIn(true)} className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm" data-testid="pseudo-save">Enregistrer</button>
          </div>
        )}

        {leaderboard.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">
            {optIn ? 'Soyez le premier du classement !' : 'Activez la participation pour voir le classement pseudonymisé.'}
          </p>
        ) : (
          <div className="space-y-1.5">
            {leaderboard.map((row) => (
              <div key={row.position} className="flex items-center gap-3 bg-slate-800/40 rounded-lg px-3 py-2" data-testid={`lb-row-${row.position}`}>
                <span className={`w-7 text-center font-bold ${row.position <= 3 ? 'text-amber-400' : 'text-slate-500'}`}>{row.position}</span>
                <img src={row.badge} alt="" className="w-8 h-8 object-contain shrink-0" />
                <span className="flex-1 min-w-0 truncate text-slate-200 text-sm">{row.pseudo}</span>
                <span className="text-xs text-slate-400 hidden sm:inline">{row.grade}</span>
                <span className="text-amber-400 font-semibold text-sm">{row.xp}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
